import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  Check, 
  Calendar, 
  Clock, 
  MapPin, 
  User, 
  Mail, 
  Phone, 
  ChevronLeft,
  ChevronRight,
  Car
} from 'lucide-react';
import axios from 'axios';
import { API_URL, api } from '../../services/api';
import Navbar from '../../components/ALLJsx/Navbar';
import Footer from '../../components/ALLJsx/Footer';
import { AppointmentBookingSkeleton } from '../../components/ALLJsx/PageSkeleton';
import '../AllCss/AppointmentBooking.css';

const DEFAULT_TIME_SLOTS = [
  '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
  '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM',
];

const AppointmentBooking = () => {
  const { stockNumber } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [car, setCar] = useState(null);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedDateClosed, setSelectedDateClosed] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0); // 0 = current week, 1-3 = next weeks
  
  const [formData, setFormData] = useState({
    appointmentType: 'test-drive',
    preQualified: null,
    date: null,
    time: null,
    firstName: user?.fullName?.split(' ')[0] || '',
    lastName: user?.fullName?.split(' ')[1] || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });

  const steps = [
    { number: 1, title: 'Choose appointment type', completed: currentStep > 1 },
    { number: 2, title: 'Optional pre-qualification', completed: currentStep > 2 },
    { number: 3, title: 'Schedule appointment', completed: currentStep > 3 },
    { number: 4, title: 'Review and confirm', completed: currentStep > 4 },
    { number: 5, title: 'Confirmation', completed: currentStep > 5 },
  ];

  const generateAvailableDates = (offset = 0) => {
    const dates = [];
    const today = new Date();
    const startDay = offset * 7; // Start of the week (0, 7, 14, 21)
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + startDay + i);
      dates.push(date);
    }
    return dates;
  };

  const availableDates = generateAvailableDates(weekOffset);

  const handlePreviousWeek = () => {
    if (weekOffset > 0) {
      setWeekOffset(weekOffset - 1);
      setFormData({ ...formData, date: null, time: null }); // Reset selection when changing weeks
    }
  };

  const handleNextWeek = () => {
    if (weekOffset < 3) {
      setWeekOffset(weekOffset + 1);
      setFormData({ ...formData, date: null, time: null }); // Reset selection when changing weeks
    }
  };

  const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const resolvedStore =
    car?.office ||
    stores.find((s) => (car?.officeId && s.id === car.officeId) || (car?.storeNumber && String(s.storeNumber) === String(car.storeNumber))) ||
    null;

  const parseOfficeHoursForDate = (date) => {
    const operatingHours = resolvedStore?.operatingHours;
    if (!operatingHours || typeof operatingHours !== 'string') {
      return { isClosed: false };
    }
    const dayLabel = DAY_LABELS[date.getDay()];
    const pattern = new RegExp(`^\\s*${dayLabel}\\s*:\\s*(.+)$`, 'i');
    const dayPart = operatingHours
      .split(';')
      .map((p) => p.trim())
      .find((p) => pattern.test(p));
    if (!dayPart) return { isClosed: false };
    const match = dayPart.match(pattern);
    const value = (match?.[1] || '').trim();
    return { isClosed: !value || /closed/i.test(value) };
  };

  const fetchCarDetails = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/cars/${stockNumber}`);
      // Handle different response structures
      const carData = response.data.car || response.data.data?.car || response.data.data || response.data;
      setCar(carData);
    } catch (error) {
      console.error('Error fetching car details:', error);
    } finally {
      setLoading(false);
    }
  }, [stockNumber]);

  const fetchBookedSlots = useCallback(async () => {
    if (!formData.date) return;
    
    setLoadingSlots(true);
    try {
      const dateStr = formData.date.toISOString().split('T')[0];
      const response = await axios.get(
        `${API_URL}/api/appointments/available-slots?date=${dateStr}&stockNumber=${stockNumber}`
      );
      // Handle both response structures
      const booked = response.data.bookedSlots || response.data.data?.bookedSlots || [];
      const openSlots = response.data.data?.availableSlots || [];
      const isClosed = Boolean(response.data.data?.isClosed);
      // Backward-compatible fallback: if backend doesn't return availableSlots yet,
      // derive them from default slots minus booked slots.
      const resolvedAvailableSlots = openSlots.length > 0 || isClosed
        ? openSlots
        : DEFAULT_TIME_SLOTS.filter((slot) => !booked.includes(slot));
      setAvailableSlots(resolvedAvailableSlots);
      setSelectedDateClosed(isClosed);
    } catch (error) {
      console.error('Error fetching booked slots:', error);
      setAvailableSlots([]);
      setSelectedDateClosed(false);
    } finally {
      setLoadingSlots(false);
    }
  }, [formData.date, stockNumber]);

  const fetchStores = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/shipping/stores`);
      const storesData = response.data?.data?.stores || [];
      setStores(Array.isArray(storesData) ? storesData : []);
    } catch (error) {
      console.error('Error fetching stores:', error);
      setStores([]);
    }
  }, []);

  useEffect(() => {
    fetchCarDetails();
  }, [fetchCarDetails]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  // Update form data when user data changes
  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        firstName: prev.firstName || user?.fullName?.split(' ')[0] || '',
        lastName: prev.lastName || user?.fullName?.split(' ')[1] || '',
        email: prev.email || user?.email || '',
        phone: prev.phone || user?.phone || '',
      }));
    }
  }, [user]);

  useEffect(() => {
    if (formData.date) {
      fetchBookedSlots();
    } else {
      setAvailableSlots([]);
      setSelectedDateClosed(false);
    }
  }, [formData.date, fetchBookedSlots]);

  // Poll for booked slots when on step 3 and a date is selected
  // This ensures slots are updated in real-time if other users book them
  useEffect(() => {
    if (currentStep === 3 && formData.date) {
      // Set up polling every 5 seconds to check for newly booked slots
      const pollInterval = setInterval(() => {
        fetchBookedSlots();
      }, 2000); // Poll every 2 seconds
      
      // Cleanup interval when component unmounts, step changes, or date changes
      return () => {
        clearInterval(pollInterval);
      };
    }
  }, [currentStep, formData.date, fetchBookedSlots]);

  useEffect(() => {
    if (formData.time && availableSlots.length > 0 && !availableSlots.includes(formData.time)) {
      setFormData((prev) => ({ ...prev, time: null }));
    }
  }, [availableSlots, formData.time]);

  const handleNext = () => {
    // Validate step 2 - must select an option
    if (currentStep === 2 && formData.preQualified === null) {
      return;
    }
    
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    setIsSubmitting(true);
    
    try {
      // Format date as YYYY-MM-DD string to avoid timezone issues
      const dateStr = formData.date ? formData.date.toISOString().split('T')[0] : null;
      
      const appointmentData = {
        stockNumber: car.stockNumber,
        carDetails: `${car.year} ${car.make} ${car.model}`,
        appointmentType: formData.appointmentType,
        date: dateStr,
        time: formData.time,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        userId: user?.id,
      };

      const response = await api.post('/appointments', appointmentData);
      
      if (response.data.success) {
      setCurrentStep(5);
        window.scrollTo(0, 0);
      } else {
        setSubmitError(response.data.message || 'Failed to book appointment. Please try again.');
      }
    } catch (error) {
      console.error('Error booking appointment:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to book appointment. Please try again.';
      setSubmitError(errorMessage);
      
      // If it's a conflict error (409), refresh the booked slots
      if (error.response?.status === 409) {
        fetchBookedSlots();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (date) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const daysShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return {
      day: days[date.getDay()],
      dayShort: daysShort[date.getDay()],
      date: date.getDate(),
      month: months[date.getMonth()],
      monthShort: monthsShort[date.getMonth()],
      fullDate: date
    };
  };

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated && !user && !loading) {
      navigate(`/auth?redirect=/appointment/${stockNumber}`, { replace: true });
    }
  }, [isAuthenticated, user, navigate, stockNumber, loading]);

  if (loading) {
    return (
      <div className="AppointmentBooking">
        <Navbar />
        <AppointmentBookingSkeleton />
        <Footer />
      </div>
    );
  }

  if (!car) {
    return <div className="appt-error">Car not found</div>;
  }

  if (!isAuthenticated || !user) {
    return <div className="appt-loading">Redirecting to login...</div>;
  }

  const carTitle = car ? `${car.year || ''} ${car.make || ''} ${car.model || ''}`.trim() || 'Vehicle' : 'Vehicle';
  const carImage = car?.images?.[0] || `${process.env.PUBLIC_URL || ''}/images/vehicle-placeholder.svg`;
  const storeName = resolvedStore?.name || '';
  const storePhone = resolvedStore?.phone || '';
  const storeAddressLine = resolvedStore?.address || '';
  const storeCity = resolvedStore?.city || '';
  const storeState = resolvedStore?.state || '';
  const storeZip = resolvedStore?.zipCode || '';
  const storeCityStateZip = [
    storeCity,
    [storeState, storeZip].filter(Boolean).join(' '),
  ].filter(Boolean).join(', ');
  const hasStoreAddress = Boolean(storeAddressLine || storeCity || storeState || storeZip);
  const mapsQuery = encodeURIComponent([storeAddressLine, storeCityStateZip].filter(Boolean).join(' '));

  return (
    <div className="appt">
      <div className="appt-container">
        <aside className="appt-sidebar">
          <div className="appt-car-preview">
            <img src={carImage} alt={carTitle} />
            <div className="appt-car-info">
              <h3>{carTitle}</h3>
              {car.trim && <p>{car.trim}</p>}
            </div>
          </div>

          <div className="appt-steps">
            <div className="appt-steps-title">{formData.appointmentType === 'test-drive' ? 'Test drive or buy' : 'Browse with expert'}</div>
            {steps.map((step) => (
              <div 
                key={step.number}
                className={`appt-step ${currentStep === step.number ? 'active' : ''} ${step.completed ? 'completed' : ''}`}
              >
                <div className="appt-step-num">
                  {step.completed ? <Check size={14} /> : step.number}
                </div>
                <div className="appt-step-txt">{step.title}</div>
              </div>
            ))}
          </div>
        </aside>

        <main className="appt-main">
          {currentStep === 1 && (
            <div className="appt-content">
              <h1>Select Your Appointment Type</h1>
              <p className="appt-desc">Choose how you'd like to proceed with your vehicle purchase</p>
              
              <div className="appt-options">
                <button
                  className={`appt-option ${formData.appointmentType === 'test-drive' ? 'selected' : ''}`}
                  onClick={() => setFormData({ ...formData, appointmentType: 'test-drive' })}
                >
                  <Car size={24} />
                  <div>
                    <h3>Test Drive & Purchase</h3>
                    <p>Experience the vehicle firsthand and complete your purchase with our streamlined process</p>
                  </div>
                </button>

                <button
                  className={`appt-option ${formData.appointmentType === 'browse' ? 'selected' : ''}`}
                  onClick={() => setFormData({ ...formData, appointmentType: 'browse' })}
                >
                  <MapPin size={24} />
                  <div>
                    <h3>Browse Inventory with Expert Guidance</h3>
                    <p>Schedule a consultation to explore our full inventory with personalized assistance from our team</p>
                  </div>
                </button>
              </div>

              <div className="appt-actions">
                <button className="appt-btn-back" onClick={() => navigate(`/cars/${stockNumber}`)}>
                  <ChevronLeft size={18} />Back to Car Details
                </button>
                <button className="appt-btn-continue" onClick={handleNext}>
                  Continue<ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="appt-content">
              <h1>Pre-Qualification</h1>
              <p className="appt-desc">Get pre-qualified to view your estimated monthly payment and streamline your purchase experience</p>

              <div className="appt-prequal">
                <button
                  className={`appt-prequal-opt ${formData.preQualified === true ? 'selected' : ''} appt-coming-soon`}
                  disabled
                >
                  <span className="appt-badge-coming-soon">Coming Soon</span>
                  <h3>Get Pre-Qualified</h3>
                  <p>Quick 2-minute process with no impact on your credit score. Receive instant financing estimates and expedite your purchase</p>
                </button>

                <button
                  className={`appt-prequal-opt ${formData.preQualified === false ? 'selected' : ''}`}
                  onClick={() => setFormData({ ...formData, preQualified: false })}
                >
                  <h3>Continue Without Pre-Qualification</h3>
                  <p>Proceed directly to scheduling your appointment. You can complete financing during your visit</p>
                </button>
              </div>

              <div className="appt-actions">
                <button className="appt-btn-back" onClick={handleBack}>
                  <ChevronLeft size={18} />Back
                </button>
                <button 
                  className="appt-btn-continue" 
                  onClick={handleNext}
                  disabled={formData.preQualified === null}
                >
                  Continue<ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="appt-content">
              <h1>Schedule your appointment</h1>

              <div className="appt-schedule">
                <div className="appt-section-header">
                  <Calendar size={20} />
                  <span>Select a date</span>
                  <div className="appt-week-nav-group">
                    <button
                      className="appt-week-nav appt-week-nav-left"
                      onClick={handlePreviousWeek}
                      disabled={weekOffset === 0}
                      aria-label="Previous week"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <button
                      className="appt-week-nav appt-week-nav-right"
                      onClick={handleNextWeek}
                      disabled={weekOffset === 3}
                      aria-label="Next week"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </div>

                <div className="appt-dates">
                  {availableDates.map((date, index) => {
                    const formatted = formatDate(date);
                    const isSelected = formData.date && formData.date.toDateString() === date.toDateString();
                    const isClosed = parseOfficeHoursForDate(date).isClosed;
                    return (
                      <button
                        key={index}
                        className={`appt-date ${isSelected ? 'selected' : ''} ${isClosed ? 'disabled' : ''}`}
                        onClick={() => {
                          if (!isClosed) {
                            setFormData({ ...formData, date, time: null });
                          }
                        }}
                        disabled={isClosed}
                        title={isClosed ? 'Store is closed on this day' : ''}
                      >
                        <div className="appt-date-day">{formatted.dayShort}</div>
                        <div className="appt-date-num">{formatted.date}</div>
                        <div className="appt-date-mon">{formatted.monthShort}</div>
                      </button>
                    );
                  })}
                </div>

                <div className="appt-section-header">
                  <Clock size={20} />
                  <span>Select a time</span>
                </div>

                <div className="appt-times">
                  {loadingSlots ? (
                    <div className="appt-loading-msg">Loading available slots...</div>
                  ) : selectedDateClosed ? (
                    <div className="appt-loading-msg">This store is closed on the selected date.</div>
                  ) : availableSlots.length === 0 ? (
                    <div className="appt-loading-msg">No available time slots for this date.</div>
                  ) : (
                    availableSlots.map((time, index) => {
                      const isSelected = formData.time === time;
                      
                      return (
                        <button
                          key={index}
                          className={`appt-time ${isSelected ? 'selected' : ''}`}
                          onClick={() => setFormData({ ...formData, time })}
                        >
                          {time}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="appt-actions">
                <button className="appt-btn-back" onClick={handleBack}>
                  <ChevronLeft size={18} />Back
                </button>
                <button 
                  className="appt-btn-continue" 
                  onClick={handleNext}
                  disabled={!formData.date || !formData.time}
                >
                  Continue<ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="appt-content">
              <h1>Review and confirm</h1>

              <div className="appt-review">
                <h3>Appointment Details</h3>
                <div className="appt-review-card-single">
                  <div className="appt-review-badge">
                    {formData.appointmentType === 'test-drive' ? 'Test drive or buy' : 'Browse the lot'}
                  </div>
                  <div className="appt-review-content">
                    {/* Left Side - Car Information */}
                    <div className="appt-review-left">
                      <div className="appt-review-vehicle">
                        <img src={carImage} alt={carTitle} />
                        <div className="appt-review-vehicle-info">
                          <h4>{carTitle}</h4>
                          <p>{car?.price ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(car.price) : ''} {car?.mileage ? `| ${car.mileage.toLocaleString()} miles` : ''}</p>
                        </div>
                      </div>
                      <div className="appt-contact-buttons">
                        {hasStoreAddress && (
                          <a 
                            href={(() => {
                              const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0 || 
                                           navigator.userAgent.toUpperCase().indexOf('MAC') >= 0;
                              return isMac 
                                ? `https://maps.apple.com/?q=${mapsQuery}`
                                : `https://www.google.com/maps/search/?api=1&query=${mapsQuery}`;
                            })()}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="appt-directions-btn"
                          >
                            <MapPin size={18} />
                            <span>Directions</span>
                            <ChevronRight size={16} />
                          </a>
                        )}
                        {storePhone && (
                          <a 
                            href={`tel:${storePhone.replace(/[^\d+]/g, '')}`}
                            className="appt-contact-btn"
                          >
                            <Phone size={18} />
                            <span>Contact</span>
                            <ChevronRight size={16} />
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Right Side - Date, Time & Address */}
                    <div className="appt-review-right">
                      <div className="appt-review-detail-item">
                        <Calendar size={18} />
                        <div>
                          <span className="appt-review-detail-label">Date & Time</span>
                          <span className="appt-review-detail-value">
                            {formData.date && formData.time ? `${formatDate(formData.date).day}, ${formatDate(formData.date).month} ${formatDate(formData.date).date} at ${formData.time.toLowerCase()}` : '-'}
                          </span>
                        </div>
                      </div>
                      <div className="appt-review-detail-item">
                        <MapPin size={18} />
                        <div>
                          <span className="appt-review-detail-label">Store</span>
                          <span className="appt-review-detail-value">
                            {storeName || 'Store details unavailable'}<br />
                            {storeAddressLine || ''}{storeAddressLine && storeCityStateZip ? <br /> : ''}
                            {storeCityStateZip || ''}
                          </span>
                        </div>
                  </div>
                  </div>
                  </div>
                </div>
              </div>

              <div className="appt-review">
                <h3>Your Information</h3>
                <div className="appt-review-card-single">
                  <div className="appt-review-content">
                    <div className="appt-review-left">
                      <div className="appt-review-detail-item">
                        <User size={18} />
                        <div>
                          <span className="appt-review-detail-label">First Name</span>
                          <span className="appt-review-detail-value">{formData.firstName || '-'}</span>
                        </div>
                      </div>
                      <div className="appt-review-detail-item">
                        <User size={18} />
                        <div>
                          <span className="appt-review-detail-label">Last Name</span>
                          <span className="appt-review-detail-value">{formData.lastName || '-'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="appt-review-right">
                      <div className="appt-review-detail-item">
                        <Mail size={18} />
                        <div>
                          <span className="appt-review-detail-label">Email</span>
                          <span className="appt-review-detail-value">{formData.email || '-'}</span>
                        </div>
                  </div>
                      <div className="appt-review-detail-item">
                        <Phone size={18} />
                        <div>
                          <span className="appt-review-detail-label">Phone</span>
                          <span className="appt-review-detail-value">{formData.phone || '-'}</span>
                  </div>
                </div>
                  </div>
                  </div>
                </div>
              </div>

              {submitError && (
                <div className="appt-error-message">
                  <span>{submitError}</span>
                </div>
              )}

              <div className="appt-actions">
                <button className="appt-btn-back" onClick={handleBack} disabled={isSubmitting}>
                  <ChevronLeft size={18} />Back
                </button>
                <button 
                  className="appt-btn-submit" 
                  onClick={handleSubmit}
                  disabled={!formData.firstName || !formData.lastName || !formData.email || !formData.phone || isSubmitting}
                >
                  {isSubmitting ? 'Confirming...' : 'Confirm Appointment'}<Check size={18} />
                </button>
              </div>
            </div>
          )}

          {currentStep === 5 && (
            <div className="appt-content appt-confirmation">
              <div className="appt-confirm-icon">
                <Check size={40} />
              </div>
              <h1>Appointment Confirmed!</h1>
              <p className="appt-confirm-msg">
                Your appointment has been successfully scheduled. We've sent a confirmation email to <strong>{formData.email}</strong>.
              </p>

              <div className="appt-confirm-details">
                <h3>Appointment Summary</h3>
                <div className="appt-confirm-item">
                  <Calendar size={18} />
                  <div>
                    <strong>Date & Time</strong>
                    <p>{formData.date && formData.time ? `${formatDate(formData.date).day}, ${formatDate(formData.date).month} ${formatDate(formData.date).date} at ${formData.time.toLowerCase()}` : '-'}</p>
                  </div>
                </div>
                <div className="appt-confirm-item">
                  <Car size={18} />
                  <div>
                    <strong>Vehicle</strong>
                    <p>{carTitle}</p>
                  </div>
                </div>
                <div className="appt-confirm-item">
                  <MapPin size={18} />
                  <div>
                    <strong>Type</strong>
                    <p>{formData.appointmentType === 'test-drive' ? 'Test drive or buy' : 'Browse the lot with expert help'}</p>
                  </div>
                </div>
              </div>

              <p className="appt-confirm-survey-hint">
                After your visit is complete, you can rate your experience from{' '}
                <Link to="/dashboard#customer-surveys">My Dashboard → Your feedback</Link>.
              </p>

              <div className="appt-confirm-actions">
                <Link to={`/cars/${stockNumber}`} className="appt-btn-secondary">
                  Back to Car Details
                </Link>
                <Link to="/inventory" className="appt-btn-primary">
                  Browse More Cars
                </Link>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AppointmentBooking;